import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

export interface OrganizationInfo {
  id: number;
  name: string;
  logo?: string;
}

interface OrganizationState {
  organization: OrganizationInfo | null;
  organizationList: OrganizationInfo[] | [];
  forceRefresh: number;
}

interface OrganizationStore {
  state: OrganizationState;
  setOrganization: (id: number, name: string, logo: string) => void;
  setOrganizationField: <K extends keyof OrganizationInfo>(
    key: K,
    value: OrganizationInfo[K],
  ) => void;
  setOrganizationList: (orgs: OrganizationInfo[]) => void;
  getOrganizationList: () => OrganizationInfo[];
  forceRefreshList: () => void;
  clearOrganization: () => void;
}

const initOrganizationState: OrganizationState = {
  organization: null,
  organizationList: [],
  forceRefresh: 0,
};

export const useOrganizationStore = create(
  persist(
    immer<OrganizationStore>((set, get) => ({
      state: initOrganizationState,

      setOrganization: (id: number, name: string, logo: string) => {
        set((store) => {
          store.state.organization = {
            id,
            name,
            logo,
          };
        });
      },

      setOrganizationField: <K extends keyof OrganizationInfo>(
        key: K,
        value: OrganizationInfo[K],
      ) => {
        set((store) => {
          if (store.state.organization) {
            (store.state.organization as OrganizationInfo)[key] = value as OrganizationInfo[K];
          }
        });
      },

      setOrganizationList: (orgs: OrganizationInfo[]) => {
        set((store) => {
          store.state.organizationList = orgs;
        });
      },

      getOrganizationList: () => {
        return get().state.organizationList;
      },

      clearOrganization: () => {
        set((store) => {
          store.state.organization = null;
        });
      },

      forceRefreshList: () => {
        set((store) => {
          store.state.forceRefresh++;
        });
      },
    })),
    {
      name: 'organization-id',
      partialize: (state) => ({
        state: {
          organization: state.state.organization,
        },
      }),
    },
  ),
);
